import { AlertCircle, CircleCheckBig, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { setTitle } from "@/hooks/useHeader.ts";
import { getSubscriptions, unsubscribe } from "@/api/subscriptions.ts";
import { Subscription } from "@/types/subscription.ts";
import SubscriptionCard from "@/components/SubscriptionCard.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { showErrorToast } from "@/lib/showErrorToast.tsx";
import { toast } from "sonner";
import { retryPayment } from "@/api/buyTickets.ts";
import { updateUser } from "@/hooks/useUser.ts";
import { useTranslation } from "@/i18n";

const Subscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>();
  const [error, setError] = useState<string>();
  const { t } = useTranslation();

  const fetchSubscriptions = async () => {
    try {
      const response = await getSubscriptions({});
      setSubscriptions(response.data.results);
      setLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      setError(t("subscriptions.fetchFailed"));
    }
  };

  useEffect(() => {
    setTitle(t("subscriptions.title"));
  }, [t]);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const onUnsubscribe = async (id: number) => {
    if (loading) return;
    setLoading(true);
    try {
      await unsubscribe({ id });
      await fetchSubscriptions();
      toast(t("subscriptions.unsubscribed"), {
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });
    } catch (_e: unknown) {
      showErrorToast(t("subscriptions.unsubscribeFailed"));
    }
    setLoading(false);
  };

  const onPayment = async (subscription: Subscription) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await retryPayment(subscription);
      if ("redirect_url" in response.data) {
        window.location.assign(response.data.redirect_url);
      } else {
        updateUser();
        toast(
          t("subscriptions.paymentSuccess", {
            count: response.data.ticket_count,
          }),
          {
            duration: 5000,
            icon: <CircleCheckBig className="size-4 text-green-600" />,
          },
        );
      }
    } catch (_e: unknown) {
      showErrorToast(t("subscriptions.paymentFailed"));
    }
    await fetchSubscriptions();
    setLoading(false);
  };

  return (
    <div className="mx-auto flex h-full max-w-md min-w-[30vw] flex-col gap-2">
      <div className="flex h-full w-full flex-col items-center gap-4 overflow-auto">
        {loading && <LoaderCircle className="size-6 animate-spin" />}
        {!loading && !subscriptions?.length && (
          <div className="m-4">{t("subscriptions.empty")}</div>
        )}
        {!!error && (
          <Alert variant="destructive" className="mx-auto my-4 w-fit">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("common.somethingWentWrong")}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {!!subscriptions?.length &&
          subscriptions.map((subscription) => (
            <SubscriptionCard
              onUnsubscribe={onUnsubscribe}
              subscription={subscription}
              onPayment={onPayment}
              key={subscription.id}
            />
          ))}
      </div>
    </div>
  );
};

export default Subscriptions;
